/**
 * Iter 166 · Communications Command Center.
 *
 * Single page (/app/admin/comms) with 4 tabs:
 *   1. Flows      — 10 outbound flows: on/off, channel, schedule, template
 *   2. Templates  — shared Handlebars-style template library
 *   3. Activity   — unified log of every send (invites/alerts/digests/etc.)
 *   4. Health     — 30-day per-channel + per-flow delivery health
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircle, Mail, Bell, Clock, Send, Sparkles, Activity,
  Heart, FileText, Copy, Trash2, Plus, Inbox as InboxIcon,
  Reply,
} from "lucide-react";
import { Link } from "react-router-dom";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { ChannelReplyDialog } from "@/components/EmailsTab";
import CronScheduleBuilder, { humanize as humanizeCron } from "@/components/CronScheduleBuilder";
import RichTextEditor from "@/components/RichTextEditor";
import TemplatesTab from "@/pages/app/comms/TemplatesTab";

const CATEGORY_META = {
  finance: { icon: FileText, label: "Finance", color: "#f97316" },
  pm: { icon: Bell, label: "PM", color: "#3b82f6" },
  marketing: { icon: Sparkles, label: "Marketing", color: "#10b981" },
  hr: { icon: MessageCircle, label: "HR", color: "#8b5cf6" },
};

const CHANNEL_ICON = { email: Mail, whatsapp: MessageCircle, in_app: Bell, both: Bell };

const KIND_BADGE = {
  email: { label: "Email", color: "#2563eb" },
  in_app: { label: "In-App", color: "#7c3aed" },
  both: { label: "Email + In-App", color: "#0891b2" },
};

// Iter 235 · Live preview helpers.
// _sampleValueFor picks a friendly default for common variable names so the
// mock email in the FlowEditor reads like a real message on first open.
// _renderTpl does client-side {{var}} substitution matching the backend regex.
const _SAMPLE_MAP = {
  first_name: "Priya",
  user_name: "Priya Sharma",
  user_email: "priya@example.com",
  contact_name: "Priya Sharma",
  client_name: "Acme Coffee Co.",
  workspace_name: "Demo Studio",
  platform_name: "Acquilix",
  hire_name: "Priya Sharma",
  owner_name: "Priya Sharma",
  manager_name: "Rahul Verma",
  role: "Team member",
  designation: "Product Designer",
  join_date: "Mar 15, 2026",
  start_date: "Mar 15, 2026",
  due_date: "Apr 10, 2026",
  invoice_no: "INV-2026-042",
  doc_no: "Q-2026-081",
  amount: "₹85,000",
  days_overdue: "12",
  days_since_sent: "5",
  missed_count: "7",
  estimated_value: "₹1,20,000",
  total_inflows: "₹18,50,000",
  total_outflows: "₹12,10,000",
  net_cash: "₹6,40,000",
  top_client: "Acme Coffee Co.",
  weeks_covered: "4",
  task_count: "3",
  top_task_titles: "Homepage redesign · Q2 launch plan · Vendor onboarding",
  setup_password_link: "https://acquilix.com/set-password?token=demo",
  reset_password_link: "https://acquilix.com/reset-password?token=demo",
  cta_link: "https://acquilix.com/get-started",
  sequence_step: "Step 2",
  goal_title: "Ship Q2 launch",
  kpis_missing: "2",
  last_week_summary: "Design system v3 shipped, 4 client reviews closed.",
};
function _sampleValueFor(name) { return _SAMPLE_MAP[name] || `<${name}>`; }
function _renderTpl(tpl, ctx) {
  if (!tpl) return "";
  return tpl.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, k) => (ctx[k] != null && ctx[k] !== "" ? String(ctx[k]) : m));
}
function _unresolvedVars(tpl, ctx) {
  const out = new Set();
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(tpl)) !== null) {
    if (!(m[1] in ctx) || ctx[m[1]] === "") out.add(m[1]);
  }
  return [...out];
}

export default function CommsCenter() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [tab, setTab] = useState("flows");

  return (
    <div className="p-6 md:p-8 space-y-5 min-h-screen" data-testid="comms-center">
      <PageHeader
        eyebrow="Communications"
        title="Communications Command Center"
        description="Every outbound email, WhatsApp, in-app notification and reminder — with configurable copy, schedules, recipients and audit log."
      />
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto"
           data-testid="comms-tabs">
        {[
          { id: "flows", label: "Messages", icon: Bell },
          { id: "templates", label: "Templates", icon: FileText },
          { id: "activity", label: "Activity Log", icon: Activity },
          { id: "health", label: "Delivery Health", icon: Heart },
          { id: "inbound", label: "Inbound Webhooks", icon: InboxIcon },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
                     className={`px-3 py-2 text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors border-b-2 ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                     data-testid={`comms-tab-${t.id}`}>
              <Icon className="h-3.5 w-3.5" />{t.label}
            </button>
          );
        })}
      </div>
      {tab === "flows" && wsId && <FlowsTab wsId={wsId} />}
      {tab === "templates" && wsId && <TemplatesTab />}
      {tab === "activity" && wsId && <ActivityTab wsId={wsId} />}
      {tab === "health" && wsId && <HealthTab wsId={wsId} />}
      {tab === "inbound" && wsId && <InboundTab wsId={wsId} />}
    </div>
  );
}

// ───────── Flows tab ─────────

function FlowsTab({ wsId }) {
  const [flows, setFlows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [kindFilter, setKindFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/comms/flows`);
      setFlows(data.flows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load flows")); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (fid, enabled) => {
    try {
      await api.patch(`/workspaces/${wsId}/comms/flows/${fid}`, { enabled });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
  };

  const filtered = flows.filter((f) => {
    if (kindFilter !== "all") {
      const k = f.kind || "email";
      if (kindFilter === "email" && !(k === "email" || k === "both")) return false;
      if (kindFilter === "in_app" && !(k === "in_app" || k === "both")) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!(f.label || "").toLowerCase().includes(q) &&
          !(f.description || "").toLowerCase().includes(q) &&
          !(f.id || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-3" data-testid="flows-container">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 border border-border rounded-md p-0.5 bg-muted/30" data-testid="flows-kind-filter">
          {[
            { id: "all", label: "All" },
            { id: "email", label: "Email" },
            { id: "in_app", label: "In-App" },
          ].map((t) => (
            <button key={t.id} type="button" onClick={() => setKindFilter(t.id)}
                     className={`px-3 py-1 text-[11px] font-medium rounded ${kindFilter === t.id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                     data-testid={`flows-kind-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search messages…"
               className="h-8 text-xs w-64" data-testid="flows-search" />
        <div className="ml-auto text-xs text-muted-foreground" data-testid="flows-count">
          {filtered.length} of {flows.length} messages
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="flows-list">
        {filtered.map((f) => {
          const cat = CATEGORY_META[f.category] || CATEGORY_META.pm;
          const CatIcon = cat.icon;
          const ChIcon = CHANNEL_ICON[f.channel] || Mail;
          const kind = f.kind || "email";
          const kbadge = KIND_BADGE[kind] || KIND_BADGE.email;
          const rcpt_count = (f.role_recipients?.length || 0)
                           + (f.user_recipients?.length || 0)
                           + (f.email_recipients?.length || 0);
          return (
            <div key={f.id} className="rounded-lg border border-border bg-card p-4"
                 data-testid={`flow-card-${f.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                     style={{ background: `${cat.color}20`, color: cat.color }}>
                  <CatIcon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-sm truncate">{f.label}</h3>
                    <Badge variant="outline" className="text-[9px]"
                            style={{ borderColor: cat.color, color: cat.color }}>
                      {cat.label}
                    </Badge>
                    <Badge variant="outline" className="text-[9px]"
                            style={{ borderColor: kbadge.color, color: kbadge.color }}
                            data-testid={`flow-kind-badge-${f.id}`}>
                      {kbadge.label}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{f.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><ChIcon className="h-3 w-3" /> {f.channel}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {humanizeCron(f.schedule || "manual")}</span>
                    <span className="flex items-center gap-1"
                          data-testid={`flow-recipients-count-${f.id}`}>
                      <FileText className="h-3 w-3" /> {rcpt_count > 0 ? `${rcpt_count} extra recipient(s)` : "Default recipients"}
                    </span>
                  </div>
                </div>
                <Switch checked={!!f.enabled}
                         onCheckedChange={(v) => toggle(f.id, v)}
                         data-testid={`flow-toggle-${f.id}`} />
              </div>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                <Button size="sm" variant="outline" className="text-xs h-7"
                         onClick={() => setEditing(f)}
                         data-testid={`flow-edit-${f.id}`}>
                  Edit
                </Button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            No messages match your filter.
          </div>
        )}
      </div>
      {editing && (
        <FlowEditor wsId={wsId} flow={editing}
                     onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function FlowEditor({ wsId, flow, onClose }) {
  const [channel, setChannel] = useState(flow.channel);
  const [schedule, setSchedule] = useState(flow.schedule || "");
  const [subject, setSubject] = useState(flow.custom_subject || "");
  const [body, setBody] = useState(flow.custom_body || "");
  const [roleRecipients, setRoleRecipients] = useState(flow.role_recipients || []);
  const [userRecipients, setUserRecipients] = useState(flow.user_recipients || []);
  const [emailRecipients, setEmailRecipients] = useState((flow.email_recipients || []).join(", "));
  const [trackingEnabled, setTrackingEnabled] = useState(flow.tracking_enabled !== false);
  const [recipientOptions, setRecipientOptions] = useState({
    static_roles: [], dynamic_tokens: [], members: [],
  });
  // Iter 235 · sampleVars powers the live preview so admins see how their
  // template will look with realistic values. Auto-seeded from the flow's
  // declared variables (`<var_name>` shown as an italic sample). Admins can
  // edit any value inline to test edge-cases (long names, empty fields, etc.).
  const [sampleVars, setSampleVars] = useState(() =>
    Object.fromEntries((flow.variables || []).map((v) => [v, _sampleValueFor(v)]))
  );
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 253i · Simulate button — resolves against live workspace state.
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/comms/recipient-options`)
      .then((r) => setRecipientOptions(r.data))
      .catch(() => { /* ignore */ });
  }, [wsId]);

  const renderedSubject = _renderTpl(subject, sampleVars);
  const renderedBody = _renderTpl(body, sampleVars);
  const unresolved = _unresolvedVars(subject + "\n" + body, sampleVars);

  const insertVar = (name) => {
    const ins = `{{${name}}}`;
    setBody((prev) => {
      if (!prev) return `<p>${ins}</p>`;
      if (/<\/p>\s*$/i.test(prev)) return prev.replace(/<\/p>\s*$/i, ` ${ins}</p>`);
      return prev + ins;
    });
  };

  const toggleRole = (val) => {
    setRoleRecipients((prev) => prev.includes(val)
      ? prev.filter((v) => v !== val)
      : [...prev, val]);
  };
  const toggleUser = (uid) => {
    setUserRecipients((prev) => prev.includes(uid)
      ? prev.filter((v) => v !== uid)
      : [...prev, uid]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const emails = emailRecipients
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.patch(`/workspaces/${wsId}/comms/flows/${flow.id}`, {
        channel, schedule, custom_subject: subject, custom_body: body,
        role_recipients: roleRecipients,
        user_recipients: userRecipients,
        email_recipients: emails,
        tracking_enabled: trackingEnabled,
      });
      toast.success("Saved");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/comms/flows/${flow.id}/test-send`,
        { subject, body, to_email: testEmail, variables: sampleVars });
      const overall = data?.result?.overall;
      if (overall === "sent") toast.success(`Test sent to ${testEmail}`);
      else {
        // Iter Feb 2026 R32 · Surface the ACTUAL reason so the user
        // can act on it (e.g. "resend_not_configured", "resend_401",
        // "no_recipients", "resend_from-domain-not-verified"). Before
        // this all failures showed as an opaque "delivery=failed".
        const emailLeg = data?.result?.email;
        const perFirst = emailLeg?.per_recipient?.[0];
        const reason = perFirst?.reason || emailLeg?.reason || "unknown";
        const detail = perFirst?.detail;
        const short = reason.length > 80 ? reason.slice(0, 80) + "…" : reason;
        toast.error(
          `Test send failed · ${short}${detail ? ` · ${String(detail).slice(0, 120)}` : ""}`,
          { duration: 12000 }
        );
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Test send failed")); }
    finally { setBusy(false); }
  };

  // Iter 253i · Resolve exactly who would receive the message right now.
  // Persists the recipient config first so the resolver sees the latest
  // values, then queries the preview endpoint with the current sampleVars
  // (so dynamic tokens like task_assignee_email resolve too).
  const doSimulate = async () => {
    setSimulating(true);
    setSimResult(null);
    try {
      const emails = emailRecipients.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      await api.patch(`/workspaces/${wsId}/comms/flows/${flow.id}`, {
        role_recipients: roleRecipients,
        user_recipients: userRecipients,
        email_recipients: emails,
      });
      const { data } = await api.post(
        `/workspaces/${wsId}/comms/flows/${flow.id}/preview-recipients`,
        { context: sampleVars });
      setSimResult(data);
      if ((data.emails?.length || 0) + (data.users?.length || 0) === 0) {
        toast.warning("No recipients resolved. Check role/user/dynamic-token config + sample variables.");
      } else {
        toast.success(`Resolved ${data.emails?.length || 0} email(s) + ${data.users?.length || 0} in-app recipient(s).`);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Simulate failed")); }
    finally { setSimulating(false); }
  };

  const kind = flow.kind || "email";

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="flow-editor">
        <DialogHeader>
          <DialogTitle>{flow.label}</DialogTitle>
          <DialogDescription>
            Configure channel, cron schedule, message template, recipients + preview.
            Use the variable chips below to inject dynamic values.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="h-8 text-xs" data-testid="flow-editor-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                  <SelectItem value="in_app">🔔 In-App</SelectItem>
                  <SelectItem value="both">Email + In-App</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Schedule</Label>
              <CronScheduleBuilder
                value={schedule}
                onChange={setSchedule}
                testid={`flow-editor-schedule-${flow.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Subject / Title</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                     placeholder={`Your ${flow.label} — {{variable}}`}
                     className="h-8 text-xs" data-testid="flow-editor-subject" />
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder="Compose the message. Use variables from the picker below."
                minHeight={200}
                testid="flow-editor-body-rich"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Variables</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {(flow.variables || []).map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)}
                           className="text-[10px] px-2 py-0.5 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground border border-border"
                           data-testid={`flow-editor-var-${v}`}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
            {/* Iter 253i · Recipients configuration */}
            <div className="border border-border rounded-md p-3 bg-muted/10 space-y-3"
                 data-testid="flow-editor-recipients">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold">Recipients</Label>
                <span className="text-[10px] text-muted-foreground">Extra recipients on top of the hardcoded ones</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Static roles</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {recipientOptions.static_roles.map((r) => {
                    const on = roleRecipients.includes(r.value);
                    return (
                      <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                              className={`text-[10px] px-2 py-1 rounded-full border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-muted border-border hover:border-primary"}`}
                              data-testid={`flow-editor-role-${r.value}`}>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Dynamic tokens</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5 mb-1">Resolved at send-time from message context (e.g. the task&apos;s assignee).</p>
                <div className="flex flex-wrap gap-1">
                  {recipientOptions.dynamic_tokens.map((r) => {
                    const on = roleRecipients.includes(r.value);
                    return (
                      <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                              className={`text-[10px] px-2 py-1 rounded-full border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-muted border-border hover:border-primary"}`}
                              data-testid={`flow-editor-token-${r.value}`}>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Specific users</Label>
                <div className="max-h-32 overflow-y-auto mt-1 border border-border rounded-md divide-y">
                  {recipientOptions.members.slice(0, 100).map((m) => {
                    const on = userRecipients.includes(m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-muted/40 cursor-pointer"
                             data-testid={`flow-editor-user-${m.id}`}>
                        <input type="checkbox" checked={on}
                               onChange={() => toggleUser(m.id)}
                               className="h-3 w-3" />
                        <span className="truncate flex-1">{m.name}</span>
                        <span className="text-[10px] text-muted-foreground truncate">{m.email}</span>
                        <Badge variant="outline" className="text-[9px] uppercase">{m.role}</Badge>
                      </label>
                    );
                  })}
                  {recipientOptions.members.length === 0 && (
                    <div className="p-3 text-[11px] text-muted-foreground italic">No workspace members loaded.</div>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">External email addresses</Label>
                <Input value={emailRecipients} onChange={(e) => setEmailRecipients(e.target.value)}
                       placeholder="alerts@client.com, cfo@partner.io"
                       className="h-8 text-xs mt-1" data-testid="flow-editor-external-emails" />
                <p className="text-[10px] text-muted-foreground mt-1">Comma-separated. Useful for CC&apos;ing clients or a shared team inbox.</p>
              </div>
              {/* Iter 253i · Simulate — resolves against live workspace state */}
              <div className="pt-2 border-t border-dashed border-border">
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline"
                            onClick={doSimulate} disabled={simulating}
                            className="h-7 text-xs"
                            data-testid="flow-editor-simulate">
                    {simulating ? "Resolving…" : "Simulate recipients"}
                  </Button>
                  <span className="text-[10px] text-muted-foreground">Shows exactly who would receive right now using live workspace state + sample variables.</span>
                </div>
                {simResult && (
                  <div className="mt-2 rounded-md border border-border bg-background p-2 space-y-2"
                       data-testid="flow-editor-simulate-result">
                    <div className="flex items-center gap-2 text-[11px]">
                      <Badge variant="outline" className="text-[9px]"
                              data-testid="simulate-total-count">
                        {simResult.unique_email_count} email · {simResult.unique_user_count} in-app
                      </Badge>
                      <span className={`text-[10px] ${simResult.enabled ? "text-emerald-600" : "text-red-600"}`}>
                        {simResult.enabled ? "Flow enabled" : "Flow disabled — no-op at send time"}
                      </span>
                    </div>
                    {simResult.emails?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Email recipients</div>
                        <div className="flex flex-wrap gap-1">
                          {simResult.emails.map((e) => (
                            <Badge key={e} variant="secondary" className="text-[10px]"
                                    data-testid={`simulate-email-${e}`}>{e}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {simResult.users?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">In-app recipients</div>
                        <div className="flex flex-wrap gap-1">
                          {simResult.users.map((u) => (
                            <Badge key={u.id} variant="secondary" className="text-[10px]"
                                    data-testid={`simulate-user-${u.id}`}>
                              {u.name} <span className="text-muted-foreground ml-1">({u.email})</span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {(simResult.emails?.length || 0) + (simResult.users?.length || 0) === 0 && (
                      <div className="text-[11px] italic text-muted-foreground">
                        No recipients resolved. Check role/user/token config and sample variables (e.g. actor_email, task_assignee_email).
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-muted/10"
                 data-testid="flow-editor-tracking">
              <Switch checked={trackingEnabled} onCheckedChange={setTrackingEnabled}
                       data-testid="flow-editor-tracking-toggle" />
              <div className="flex-1">
                <Label className="text-xs font-medium">Open + click tracking</Label>
                <p className="text-[10px] text-muted-foreground">Injects a 1×1 pixel and rewrites links so opens/clicks land in Activity Log.</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="border border-border bg-muted/20">
              <div className="px-3 py-1.5 border-b border-border flex items-center gap-2 bg-muted/30">
                <span className="text-[10px] uppercase tracking-widest font-semibold">Live preview</span>
                <span className="text-[10px] text-muted-foreground">as recipients will see it</span>
                {kind !== "email" && (
                  <Badge variant="outline" className="ml-auto text-[9px]">{KIND_BADGE[kind].label}</Badge>
                )}
              </div>
              {/* Mock email frame */}
              <div className="bg-background m-2 border border-border" data-testid="flow-editor-live-preview">
                <div className="px-3 py-2 border-b border-border bg-muted/20 text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-mono">Acquilix &lt;team@acquilix.com&gt;</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-muted-foreground">To:</span>
                    <span className="font-mono">{sampleVars.user_email || sampleVars.email || "recipient@example.com"}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-muted-foreground">Subject:</span>
                    <span className="font-semibold" data-testid="flow-editor-preview-subject">{renderedSubject || <span className="italic text-muted-foreground">(empty subject)</span>}</span>
                  </div>
                </div>
                <div className="px-4 py-3 min-h-[160px] max-h-[320px] overflow-y-auto prose prose-sm max-w-none dark:prose-invert text-sm smart-rich-text-content"
                     data-testid="flow-editor-preview-body"
                     dangerouslySetInnerHTML={{ __html: renderedBody || "<p class='text-muted-foreground italic'>(empty body)</p>" }} />
              </div>
              {unresolved.length > 0 && (
                <div className="px-3 pb-2 text-[10px] text-amber-700" data-testid="flow-editor-preview-unresolved">
                  Unresolved: {unresolved.join(", ")}
                </div>
              )}
            </div>
            {(flow.variables || []).length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 inline-block">Preview values · edit to test edge cases</Label>
                <div className="border border-border divide-y divide-border" data-testid="flow-editor-sample-vars">
                  {(flow.variables || []).map((v) => (
                    <div key={v} className="flex items-center gap-2 px-2 py-1.5">
                      <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0 truncate">{`{{${v}}}`}</span>
                      <Input
                        value={sampleVars[v] ?? ""}
                        onChange={(e) => setSampleVars((prev) => ({ ...prev, [v]: e.target.value }))}
                        className="h-7 text-xs"
                        placeholder={_sampleValueFor(v)}
                        data-testid={`flow-editor-sample-${v}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1">
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                       placeholder="Send test to real email…" className="h-8 text-xs"
                       data-testid="flow-editor-test-email" />
                <Button size="sm" onClick={doTest} disabled={busy || !testEmail}
                         data-testid="flow-editor-test-send">
                  <Send className="h-3 w-3 mr-1" /> Test
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="flow-editor-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────── Activity tab ─────────

function ActivityTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("sent");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      if (channelFilter) params.set("channel", channelFilter);
      if (eventTypeFilter && eventTypeFilter !== "sent") params.set("event_type", eventTypeFilter);
      const { data } = await api.get(
        `/workspaces/${wsId}/comms/activity?${params.toString()}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load activity")); }
  }, [wsId, kindFilter, channelFilter, eventTypeFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="activity-list">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="activity-event-filter"><SelectValue placeholder="Event" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sent">Sends</SelectItem>
            <SelectItem value="opened">Opens</SelectItem>
            <SelectItem value="clicked">Clicks</SelectItem>
            <SelectItem value="all">All events</SelectItem>
          </SelectContent>
        </Select>
        <Select value={channelFilter || "_all"} onValueChange={(v) => setChannelFilter(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="activity-channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Any channel</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Input value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
               placeholder="Filter by kind (e.g. quote, risk_digest)"
               className="h-8 text-xs w-64" data-testid="activity-kind-filter" />
        <div className="ml-auto text-xs text-muted-foreground">{rows.length} events</div>
      </div>
      <div className="rounded-lg border border-border bg-card divide-y">
        {rows.map((r) => {
          const et = r.event_type || "sent";
          const overall = r.delivery?.overall || et;
          const tone = et === "opened" ? "text-sky-600"
            : et === "clicked" ? "text-violet-600"
            : overall === "sent" ? "text-emerald-600"
            : overall === "failed" ? "text-red-600" : "text-amber-600";
          return (
            <div key={r.id} className="p-3 text-xs" data-testid={`activity-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">{r.doc_kind}</span>
                <span className="font-semibold truncate flex-1">{r.subject || r.click_url || r.doc_no || "—"}</span>
                <span className={`font-mono text-[10px] uppercase ${tone}`}>{et}</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                <span>{new Date(r.created_at).toLocaleString()}</span>
                <span>·</span>
                <span>{r.channel || "—"}</span>
                {r.to_emails?.length > 0 && (<><span>·</span><span className="truncate">{r.to_emails.join(", ")}</span></>)}
                {r.whatsapp_to && (<><span>·</span><span>{r.whatsapp_to}</span></>)}
                {r.click_url && (<><span>·</span><span className="truncate max-w-xs">→ {r.click_url}</span></>)}
                {et === "sent" && (
                  <div className="ml-auto flex items-center gap-2">
                    <ReplyPreviewHover wsId={wsId} eventId={r.id} />
                    <Link
                      to={"/app/inbox"}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      data-testid={`activity-inbox-link-${r.id}`}
                      title="Open Inbox — view replies to this thread there"
                    >
                      <InboxIcon className="w-3 h-3" /> View in Inbox
                    </Link>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No activity yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ───────── Health tab ─────────

function HealthTab({ wsId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/comms/health`)
      .then((r) => setData(r.data))
      .catch(() => { /* ignore */ });
  }, [wsId]);
  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground">Loading health…</div>;

  const chRows = Object.entries(data.per_channel || {})
    .filter(([, v]) => v.total > 0);
  const flowRows = Object.entries(data.per_flow || {})
    .sort((a, b) => (b[1].total || 0) - (a[1].total || 0));

  return (
    <div className="space-y-4" data-testid="health-panel">
      <div className="text-xs text-muted-foreground">
        Aggregated over the last {data.days} days.
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Per channel</h3>
          {chRows.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No sends in this window.</div>
          ) : chRows.map(([ch, v]) => {
            const pct = v.total > 0 ? Math.round((v.sent / v.total) * 100) : 0;
            return (
              <div key={ch} className="mb-3" data-testid={`health-channel-${ch}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium capitalize">{ch}</span>
                  <span className="font-mono">{v.sent}/{v.total} sent · {pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted mt-1 overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, background: pct > 80 ? "#10b981" : pct > 50 ? "#f59e0b" : "#ef4444" }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Per flow (top 12)</h3>
          <div className="space-y-1.5 text-xs">
            {flowRows.slice(0, 12).map(([kind, v]) => (
              <div key={kind} className="flex items-center justify-between border-b border-border/40 pb-1"
                    data-testid={`health-flow-${kind}`}>
                <span className="truncate font-mono text-[10px]">{kind}</span>
                <span className="font-mono text-muted-foreground">
                  <span className="text-emerald-600">{v.sent}</span>
                  /<span className="text-amber-600">{v.skipped}</span>
                  /<span className="text-red-600">{v.failed}</span>
                  <span className="text-muted-foreground"> ({v.total})</span>
                </span>
              </div>
            ))}
            {flowRows.length === 0 && (<div className="text-muted-foreground italic">No flow data yet.</div>)}
          </div>
          <div className="text-[10px] text-muted-foreground mt-3">
            Format: <span className="text-emerald-600">sent</span> / <span className="text-amber-600">skipped</span> / <span className="text-red-600">failed</span> (total)
          </div>
        </div>
      </div>
    </div>
  );
}


// ───────── Reply Preview hover-card (Iter 167c) ─────────

function ReplyPreviewHover({ wsId, eventId }) {
  const [state, setState] = useState({ loaded: false, loading: false,
                                          hasReply: false, message: null,
                                          minutesSinceSend: null });
  const [replyTid, setReplyTid] = useState(null);
  const load = useCallback(async () => {
    if (state.loaded || state.loading) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/comms/activity/${eventId}/reply-preview`);
      setState({ loaded: true, loading: false,
                    hasReply: !!data.has_reply,
                    message: data.message || null,
                    minutesSinceSend: data.minutes_since_send });
    } catch (e) {
      setState({ loaded: true, loading: false, hasReply: false, message: null,
                    minutesSinceSend: null });
    }
  }, [wsId, eventId, state.loaded, state.loading]);

  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button"
                 onFocus={load}
                 onMouseEnter={load}
                 className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                 data-testid={`activity-reply-preview-${eventId}`}
                 title="Hover to peek any reply">
          <Reply className="w-3 h-3" /> Reply?
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-96 text-xs" data-testid={`reply-preview-content-${eventId}`}>
        {state.loading && <div className="text-muted-foreground">Checking for replies…</div>}
        {!state.loading && state.loaded && !state.hasReply && (
          <div className="text-muted-foreground">No reply received yet from this recipient.</div>
        )}
        {!state.loading && state.loaded && state.hasReply && state.message && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{state.message.from_name || state.message.from_email || state.message.from_phone}</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
                {state.minutesSinceSend != null
                  ? `${state.minutesSinceSend < 60
                      ? `${state.minutesSinceSend}m`
                      : state.minutesSinceSend < 24 * 60
                        ? `${Math.round(state.minutesSinceSend / 60)}h`
                        : `${Math.round(state.minutesSinceSend / 1440)}d`} after send`
                  : ""}
              </span>
            </div>
            {state.message.subject && (
              <div className="font-medium truncate">{state.message.subject}</div>
            )}
            <div className="text-muted-foreground whitespace-pre-wrap line-clamp-6">
              {state.message.snippet || state.message.body_text || "(empty)"}
            </div>
            {state.message.thread_id && (
              <div className="flex items-center gap-3">
                {state.message.channel !== "gmail" && (
                  <button
                    type="button"
                    onClick={() => setReplyTid(state.message.thread_id)}
                    className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
                    data-testid={`reply-preview-reply-btn-${eventId}`}
                    title="Reply without leaving the activity log"
                  >
                    <Reply className="w-3 h-3" /> Reply
                  </button>
                )}
                <Link to={`/app/inbox?thread=${state.message.thread_id}`}
                       className="text-primary hover:underline inline-flex items-center gap-1"
                       data-testid={`reply-preview-open-thread-${eventId}`}>
                  <InboxIcon className="w-3 h-3" /> Open thread
                </Link>
              </div>
            )}
          </div>
        )}
      </HoverCardContent>
      {replyTid && (
        <ChannelReplyDialog
          wsId={wsId}
          tid={replyTid}
          onClose={() => setReplyTid(null)}
          onSent={() => { setReplyTid(null); toast.success("Reply sent"); }}
        />
      )}
    </HoverCard>
  );
}


// ───────── Inbound Webhooks tab ─────────

function InboundTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/comms/inbound-secrets`);
      setRows(data.secrets || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load inbound secrets"));
    } finally { setBusy(false); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const rotate = async (channel) => {
    if (!window.confirm("Rotate the secret? Existing configured webhooks will stop working until you update them.")) return;
    try {
      await api.post(`/workspaces/${wsId}/comms/inbound-secrets/rotate/${channel}`);
      toast.success("Secret rotated");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Rotate failed"));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (busy) return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="inbound-webhooks-tab">
      <div className="text-xs text-muted-foreground max-w-2xl">
        Configure these URLs in your Resend and Interakt consoles so inbound replies land in your unified Inbox alongside Gmail. Replies from clients appear under the same thread — filterable in Inbox by channel.
      </div>
      <div className="rounded-lg border border-border bg-card divide-y">
        {rows.map((r) => {
          // R28 · `r.query` may be empty (Interakt uses ws_id in the path,
          // no token) — concat unconditionally.
          const fullUrl = `${backendUrl}${r.path}${r.query || ""}`;
          return (
            <div key={r.channel} className="p-4 space-y-2" data-testid={`inbound-row-${r.channel}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{r.label}</span>
                <Badge variant="outline" className="text-[10px] uppercase font-mono">{r.channel}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => copy(fullUrl)}
                          data-testid={`inbound-copy-${r.channel}`}>
                    <Copy className="w-3 h-3 mr-1" /> Copy URL
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => rotate(r.channel)}
                          data-testid={`inbound-rotate-${r.channel}`}>
                    Rotate secret
                  </Button>
                </div>
              </div>
              <div className="font-mono text-[11px] break-all bg-muted/50 border border-border p-2 rounded">
                {fullUrl}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {r.channel === "resend_inbound"
                  ? "In the Resend dashboard, add an Inbound Route pointing to the URL above. Any email replying to a message sent via Acquilix will be recorded as a new inbound thread in Inbox."
                  : "In your Interakt (or WhatsApp BSP) console, set the incoming-message webhook to the URL above. One thread per contact phone."}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No inbound channels configured.
          </div>
        )}
      </div>
    </div>
  );
}
